import React from 'react';
import {
  Box,
  Typography,
  Tooltip,
  useTheme,
  alpha
} from '@mui/material';
import { AccessTime, Person, Receipt } from '@mui/icons-material';
import { SessionTimeline, formatCurrency, formatDuration, getPaymentStatusInfo } from '../../api/invoiceApi';

interface SessionBarProps {
  session: SessionTimeline;
  onClick?: (sessionId: number) => void;
  /** Width percentage for visual timeline (0-100) */
  widthPercent?: number;
  /** Left offset percentage for timeline positioning (0-100) */
  leftPercent?: number;
  /** Whether to show as a mini bar (compact mode) */
  compact?: boolean;
}

/**
 * SessionBar - Visual bar representing a session in the timeline.
 * Shows session duration, amount, and payment status with color coding.
 */
const SessionBar: React.FC<SessionBarProps> = ({
  session,
  onClick,
  widthPercent = 100,
  leftPercent = 0,
  compact = false
}) => {
  const theme = useTheme();
  const statusInfo = getPaymentStatusInfo(session.paymentStatus);
  const isActive = session.endedAt === null;

  const handleClick = () => {
    onClick?.(session.id);
  };

  const formatTime = (isoString: string) => {
    return new Date(isoString).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const tooltipContent = (
    <Box sx={{ p: 0.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {session.sessionCode}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block' }}>
        {formatTime(session.startedAt)} - {session.endedAt ? formatTime(session.endedAt) : 'Active'}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block' }}>
        Duration: {formatDuration(session.durationMinutes)}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block' }}>
        Amount: {formatCurrency(session.totalAmount)}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block' }}>
        Orders: {session.orders.length}
      </Typography>
      {session.customerName && (
        <Typography variant="caption" sx={{ display: 'block' }}>
          Customer: {session.customerName}
        </Typography>
      )}
    </Box>
  );

  if (compact) {
    // Compact mode for timeline view
    return (
      <Tooltip title={tooltipContent} arrow placement="top">
        <Box
          onClick={handleClick}
          sx={{
            position: 'absolute',
            left: `${leftPercent}%`,
            width: `${Math.max(widthPercent, 2)}%`,
            height: 24,
            backgroundColor: alpha(statusInfo.color, 0.8),
            borderRadius: 0.5,
            cursor: onClick ? 'pointer' : 'default',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            transition: 'all 0.2s',
            '&:hover': onClick ? {
              transform: 'scaleY(1.2)',
              zIndex: 10,
              boxShadow: theme.shadows[4]
            } : {},
            ...(isActive && {
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.7 }
              }
            })
          }}
        >
          {widthPercent > 10 && (
            <Typography
              variant="caption"
              sx={{
                color: 'var(--text-inverse)',
                fontWeight: 600,
                fontSize: '0.65rem',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                px: 0.5
              }}
            >
              {formatCurrency(session.totalAmount)}
            </Typography>
          )}
        </Box>
      </Tooltip>
    );
  }

  // Full mode - detailed session card
  return (
    <Box
      onClick={handleClick}
      sx={{
        p: 1.5,
        borderRadius: 1.5,
        border: `1px solid ${alpha(statusInfo.color, 0.3)}`,
        backgroundColor: alpha(statusInfo.color, 0.05),
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s',
        '&:hover': onClick ? {
          backgroundColor: alpha(statusInfo.color, 0.1),
          borderColor: statusInfo.color,
          boxShadow: theme.shadows[2]
        } : {},
        ...(isActive && {
          borderWidth: 2,
          borderStyle: 'solid',
          borderColor: statusInfo.color
        })
      }}
    >
      {/* Header Row */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {session.sessionCode}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
            <AccessTime sx={{ fontSize: 12, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {formatTime(session.startedAt)} - {session.endedAt ? formatTime(session.endedAt) : 'Active'}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            px: 1,
            py: 0.25,
            borderRadius: 1,
            backgroundColor: alpha(statusInfo.color, 0.15),
            color: statusInfo.color
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {statusInfo.text}
          </Typography>
        </Box>
      </Box>

      {/* Stats Row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
          {formatCurrency(session.totalAmount)}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Receipt sx={{ fontSize: 14, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            {session.orders.length} order{session.orders.length !== 1 ? 's' : ''}
          </Typography>
        </Box>

        <Typography variant="caption" color="text.secondary">
          {formatDuration(session.durationMinutes)}
        </Typography>

        {session.customerName && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
            <Person sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                maxWidth: 100,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {session.customerName}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SessionBar;
